import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QrCode, X, Loader2, Camera, Keyboard, ScanLine, Zap, ZapOff } from "lucide-react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";

/**
 * QRScannerModal — WebRTC camera scanner + manual entry fallback.
 *
 * Reads QR codes embedded in order-sheet, karigar-job-bag and bag-label PDFs.
 * Backend resolves the code to the correct in-app route based on the user's
 * role (production users get sent straight to the job page; others land on the
 * order page).
 */
export function QRScannerModal({ open, onClose }) {
  const [mode, setMode] = useState("camera"); // camera | manual
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [torch, setTorch] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const readerRef = useRef(null);
  const containerId = "qr-reader-container";
  const navigate = useNavigate();

  // Start / stop the camera lifecycle
  useEffect(() => {
    if (!open || mode !== "camera") return;
    let stopped = false;
    let scanner = null;

    const start = async () => {
      setStatus("Requesting camera…");
      try {
        // Ensure DOM node is rendered before we bind
        await new Promise((r) => requestAnimationFrame(r));
        scanner = new Html5Qrcode(containerId, { verbose: false });
        readerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
          async (decoded) => {
            if (stopped) return;
            stopped = true;
            // Vibrate on scan (mobile). navigator.vibrate is Chromium-only but
            // silently no-ops elsewhere.
            try { navigator.vibrate?.(120); } catch (_) { /* noop */ }
            setStatus("Resolving…");
            await resolve(decoded);
          },
          () => { /* scan tick errors are noisy — ignore */ }
        );
        setStatus("Point the camera at a TARU QR code.");
        // Probe torch capability
        try {
          const caps = await scanner.getRunningTrackCapabilities?.();
          setTorchAvailable(Boolean(caps && caps.torch));
        } catch (_) { /* older API */ }
      } catch (e) {
        setStatus("");
        toast.error(e?.message?.includes("Permission") ? "Camera permission denied" : "Camera unavailable — use manual entry");
        setMode("manual");
      }
    };

    start();

    return () => {
      stopped = true;
      const s = readerRef.current;
      if (s) {
        s.stop().then(() => s.clear()).catch(() => { /* noop */ });
        readerRef.current = null;
      }
    };
  }, [open, mode]);

  const resolve = async (text) => {
    setBusy(true);
    try {
      const { data } = await api.post("/copilot/qr/resolve", { text });
      if (data.ok && data.path) {
        toast.success(`Opening ${data.path}`);
        onClose?.();
        navigate(data.path);
      } else {
        toast.error(data.reason || "Unrecognised code");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => onClose?.()} data-testid="qr-scanner-modal">
      <div className="w-full max-w-md bg-[#FAF7EF] rounded-3xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 bg-[#083F22] text-white flex items-center gap-2">
          <ScanLine size={18} className="text-[#C9A24B]" />
          <div className="font-serif text-lg">Scan QR</div>
          <button onClick={() => onClose?.()} className="ml-auto p-2 rounded-full hover:bg-white/10" data-testid="qr-close">
            <X size={18} />
          </button>
        </div>

        {mode === "camera" ? (
          <div className="p-3">
            <div id={containerId} className="w-full aspect-square bg-black rounded-2xl overflow-hidden" data-testid="qr-camera"/>
            <p className="text-xs text-[#4A5A50] text-center mt-3">{status || "Starting camera…"}</p>
            <div className="flex justify-center gap-2 mt-3">
              {torchAvailable && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    const next = !torch;
                    setTorch(next);
                    try { await readerRef.current?.applyVideoConstraints?.({ advanced: [{ torch: next }] }); }
                    catch (_) { toast.warning("Torch not supported by this camera"); setTorch(!next); }
                  }}
                  className={`rounded-full ${torch ? "bg-[#C9A24B] text-[#0F1E15] hover:bg-[#B88C34]" : ""}`}
                  data-testid="qr-torch-toggle"
                >
                  {torch ? <ZapOff size={14} className="mr-2"/> : <Zap size={14} className="mr-2"/>}
                  {torch ? "Torch off" : "Torch"}
                </Button>
              )}
              <Button variant="outline" onClick={() => setMode("manual")} className="rounded-full" data-testid="qr-switch-manual">
                <Keyboard size={14} className="mr-2"/> Type code instead
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); if (manual.trim()) resolve(manual.trim()); }}
            className="p-4 space-y-3"
          >
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Paste order/track/job code</label>
              <Input
                autoFocus
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder="e.g. ORD-2026-001 or track:abc or a full URL"
                className="mt-1"
                data-testid="qr-manual-input"
              />
            </div>
            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => setMode("camera")} className="rounded-full" data-testid="qr-switch-camera">
                <Camera size={14} className="mr-2"/> Use camera
              </Button>
              <Button type="submit" disabled={!manual.trim() || busy} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="qr-manual-submit">
                {busy ? <Loader2 size={14} className="animate-spin mr-2"/> : <ScanLine size={14} className="mr-2"/>} Open
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


/**
 * Small button component to open the scanner — used from AppShell top bar.
 */
export function QRScannerButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="hidden sm:inline-flex items-center gap-1 px-3 py-2 rounded-full border border-[#E4DFCF] bg-white hover:border-[#0F5E39]/40 hover:bg-[#F6F1E1] text-[#0F5E39] text-sm"
        aria-label="Scan QR"
        data-testid="qr-scanner-open"
      >
        <QrCode size={16}/>
        <span className="hidden md:inline text-xs uppercase tracking-widest">Scan</span>
      </button>
      <button
        onClick={() => setOpen(true)}
        className="sm:hidden p-2 rounded-full hover:bg-[#F1EDDF]"
        title="Scan QR"
        data-testid="qr-scanner-open-mobile"
      >
        <QrCode size={20} className="text-[#0F5E39]"/>
      </button>
      <QRScannerModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
